export type MosaicLayoutKind = "hero" | "half" | "cell";

/** Matches `gap-3` (0.75rem) / `minmax(11rem, …)` on the Updates mosaic. */
const MOSAIC_GAP_REM = 0.75;
const MOSAIC_ROW_UNIT_REM = 11;
/** Tailwind `2xl` — tiles switch to larger type there, so rows need more height. */
export const MOSAIC_WIDE_QUERY = "(min-width: 1536px)";
const MOSAIC_ROW_UNIT_WIDE_REM = 12.5;

function rootFontSizePx(): number {
  if (typeof document === "undefined") return 16;
  const parsed = Number.parseFloat(
    getComputedStyle(document.documentElement).fontSize,
  );
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 16;
}

/** Gap in px for the current board `html` font-size. */
export function mosaicGapPx(): number {
  return MOSAIC_GAP_REM * rootFontSizePx();
}

/** Row unit in px for the current board `html` font-size (11rem; 12.5rem on 2xl). */
export function mosaicRowUnitPx(): number {
  const isWide =
    typeof window !== "undefined" && window.matchMedia(MOSAIC_WIDE_QUERY).matches;
  return (isWide ? MOSAIC_ROW_UNIT_WIDE_REM : MOSAIC_ROW_UNIT_REM) * rootFontSizePx();
}

/** @deprecated Prefer mosaicGapPx() so layout tracks board font-size. */
export const MOSAIC_GAP_PX = 12;
/** @deprecated Prefer mosaicRowUnitPx() so layout tracks board font-size. */
export const MOSAIC_ROW_UNIT_PX = 176;

/**
 * 3-column masonry spans — relevancy grows height only (never wider).
 * - hero / half: 1 wide × 2 tall (elevated relevancy)
 * - cell: 1 × 1 (baseline)
 *
 * Placement clamps col/row span to the top-left hole so a tall tile
 * can shrink when only a short gap remains — otherwise holes stay empty
 * until a later page of shorter tiles arrives.
 */
export const MOSAIC_SPAN_CELLS: Record<
  MosaicLayoutKind,
  { colSpan: number; rowSpan: number }
> = {
  hero: { colSpan: 1, rowSpan: 2 },
  half: { colSpan: 1, rowSpan: 2 },
  cell: { colSpan: 1, rowSpan: 1 },
};

export interface MosaicPlacement {
  index: number;
  col: number;
  row: number;
  colSpan: number;
  rowSpan: number;
}

/**
 * CSS Grid auto-flow:row placement for the Updates mosaic spans.
 * Preserves input order; always fills the top-left empty cell first.
 */
export function placeMosaicLayouts(
  layouts: MosaicLayoutKind[],
  columns: number,
): { placements: MosaicPlacement[]; rowCount: number } {
  const occupied = new Set<string>();
  const placements: MosaicPlacement[] = [];
  let rowCount = 0;

  const key = (row: number, col: number) => `${row},${col}`;
  const maxScan = layouts.length * 4 + 4;

  const fits = (row: number, col: number, colSpan: number, rowSpan: number) => {
    if (col + colSpan > columns) return false;
    for (let r = row; r < row + rowSpan; r++) {
      for (let c = col; c < col + colSpan; c++) {
        if (occupied.has(key(r, c))) return false;
      }
    }
    return true;
  };

  const mark = (row: number, col: number, colSpan: number, rowSpan: number) => {
    for (let r = row; r < row + rowSpan; r++) {
      for (let c = col; c < col + colSpan; c++) {
        occupied.add(key(r, c));
      }
    }
  };

  const findTopLeftEmpty = (): { row: number; col: number } | null => {
    for (let row = 0; row < maxScan; row++) {
      for (let col = 0; col < columns; col++) {
        if (!occupied.has(key(row, col))) return { row, col };
      }
    }
    return null;
  };

  const emptyRunWidth = (row: number, col: number) => {
    let width = 0;
    while (col + width < columns && !occupied.has(key(row, col + width))) {
      width++;
    }
    return width;
  };

  for (let index = 0; index < layouts.length; index++) {
    const layout = layouts[index] ?? "cell";
    const span = MOSAIC_SPAN_CELLS[layout];
    const hole = findTopLeftEmpty();
    if (!hole) break;

    const available = emptyRunWidth(hole.row, hole.col);
    let colSpan = Math.min(span.colSpan, available, columns);
    let rowSpan = span.rowSpan;

    // Shrink until the tile fits this hole (e.g. hero into a 1-col gap).
    while (colSpan >= 1 && !fits(hole.row, hole.col, colSpan, rowSpan)) {
      if (rowSpan > 1) {
        rowSpan -= 1;
      } else if (colSpan > 1) {
        colSpan -= 1;
        rowSpan = span.rowSpan;
      } else {
        break;
      }
    }

    if (!fits(hole.row, hole.col, colSpan, rowSpan)) {
      // Fallback: first global fit (should be rare after clamping).
      let placed = false;
      for (let row = 0; !placed && row < maxScan; row++) {
        for (let col = 0; col <= columns - 1; col++) {
          const trySpan = Math.min(span.colSpan, columns - col);
          if (!fits(row, col, trySpan, span.rowSpan)) continue;
          mark(row, col, trySpan, span.rowSpan);
          placements.push({
            index,
            col,
            row,
            colSpan: trySpan,
            rowSpan: span.rowSpan,
          });
          rowCount = Math.max(rowCount, row + span.rowSpan);
          placed = true;
          break;
        }
      }
      continue;
    }

    mark(hole.row, hole.col, colSpan, rowSpan);
    placements.push({
      index,
      col: hole.col,
      row: hole.row,
      colSpan,
      rowSpan,
    });
    rowCount = Math.max(rowCount, hole.row + rowSpan);
  }

  return { placements, rowCount };
}

export function mosaicPlacementStyle(
  placement: MosaicPlacement,
  columns: number,
  gridWidth: number,
): { top: number; left: number; width: number; height: number } {
  const gap = mosaicGapPx();
  const rowUnit = mosaicRowUnitPx();
  const colPitch = (gridWidth - gap * (columns - 1)) / columns;
  const rowPitch = rowUnit + gap;
  return {
    top: placement.row * rowPitch,
    left: placement.col * (colPitch + gap),
    width: placement.colSpan * colPitch + (placement.colSpan - 1) * gap,
    height: placement.rowSpan * rowUnit + (placement.rowSpan - 1) * gap,
  };
}

export function mosaicGridHeight(rowCount: number): number {
  if (rowCount <= 0) return 0;
  return rowCount * mosaicRowUnitPx() + (rowCount - 1) * mosaicGapPx();
}
